import { kebabCase, partition } from 'es-toolkit';
import React, { ReactElement, ReactNode, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Breakpoint,
  Button,
  CircularProgress,
  Dialog as MaterialDialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  SxProps,
  Theme,
} from '@mui/material';

import { getPopupStateMonitor } from './monitoring';
import { closeCurrentPopup } from './popupSlice';

type ButtonClickHandler = () => void;

/** While a returned promise is pending, the buttons are disabled and the spinner is shown. */
type SubmitHandler = () => void | Promise<unknown>;

/** Which slot a dialog button occupies. Deliberately a closed set, because it is reported
 *  as a usage metric: titles and button labels are free-form display strings -- some
 *  interpolate user-typed content, e.g. `Share ${componentSet.name}` -- so they must never
 *  become metric attributes. Add an explicit bounded prop if per-dialog attribution is
 *  wanted later; do not derive one from what is rendered. */
type DialogButtonRole = 'submit' | 'cancel' | 'other';

interface Props {
  readonly title?: string | ReactElement;
  readonly scrollPaper?: boolean;
  readonly insistent?: boolean;

  readonly open?: boolean;
  readonly disabled?: boolean;
  readonly hasSpinner?: boolean;
  readonly hasTransition?: boolean /** default is true */;

  readonly submitText?: string;
  readonly cancelText?: string;

  readonly submitDisabled?: boolean;
  readonly cancelDisabled?: boolean;

  readonly onClose?: ButtonClickHandler;
  readonly onSubmit?: SubmitHandler;
  readonly onCancel?: ButtonClickHandler | 'close';

  readonly otherButtons?: {
    readonly [buttonName: string]: { handler: ButtonClickHandler; containedVariant?: true };
  };
  readonly enterKeySubmits?: boolean;

  readonly variant?: 'markup' | 'design';

  readonly sx?: SxProps<Theme>;
  readonly slotProps?: DialogProps['slotProps'];
  readonly maxWidth?: Breakpoint;
  readonly contentSx?: SxProps<Theme>;
  readonly fullWidth?: boolean;
  readonly fullScreen?: boolean;

  readonly disableRestoreFocus?: boolean;

  readonly actionsNode?: ReactNode;

  readonly children?: ReactNode;
}

export default function Dialog({
  title,
  scrollPaper,
  insistent,
  open,
  disabled,
  hasSpinner,
  hasTransition = true,
  submitText,
  cancelText = 'Cancel',
  submitDisabled,
  cancelDisabled,
  onClose: propsOnClose,
  onSubmit,
  onCancel,
  otherButtons = {},
  enterKeySubmits,
  variant = 'markup',
  sx,
  slotProps,
  maxWidth,
  contentSx,
  fullWidth,
  fullScreen,
  disableRestoreFocus,
  actionsNode,
  children,
}: Props) {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (open === false) return null;

  const submit =
    onSubmit &&
    (() => {
      if (isSubmitting || disabled || submitDisabled) return;

      const result = onSubmit();
      if (result instanceof Promise) {
        setIsSubmitting(true);
        void result.finally(() => setIsSubmitting(false));
      }
    });

  const onClose = propsOnClose ?? (() => void dispatch(closeCurrentPopup()));

  const contentIsText =
    typeof children === 'string' || (Array.isArray(children) && children.every((child) => typeof child === 'string'));

  function renderDialogButton(
    role: DialogButtonRole,
    text: string,
    handler: ButtonClickHandler,
    forceDisabled?: boolean,
    idOverride?: string,
    variantOverride?: 'contained' | 'outlined',
  ) {
    const id = idOverride ?? `dialog-${kebabCase(text)}`;

    return (
      <Button
        size="small"
        variant={(variantOverride ?? text === (submitText ?? 'Ok')) ? 'contained' : 'outlined'}
        sx={[
          variant !== 'markup' ? { my: 1, py: 1 } : {},
          variant === 'markup'
            ? {
                '&:hover': {
                  bgcolor: 'primary.dark',
                  color: '#fff',
                },
              }
            : {},
        ]}
        disabled={disabled || isSubmitting || forceDisabled}
        onClick={() => {
          getPopupStateMonitor()?.recordInteraction('Dialog button clicked', { actionType: role });
          handler();
        }}
        key={text}
        id={id}
      >
        {text}
      </Button>
    );
  }

  const spinner =
    hasSpinner || isSubmitting ? (
      <Box sx={{ ml: 4, mr: 'auto' }}>
        <CircularProgress />
      </Box>
    ) : null;

  const [containedButtons, outlinedButtons] = partition(
    Object.entries(otherButtons),
    ([_, handler]) => handler.containedVariant ?? false,
  );

  const buttons = outlinedButtons.map(([text, info]) => renderDialogButton('other', text, info.handler));

  if (onCancel === 'close') {
    buttons.push(renderDialogButton('cancel', cancelText, onClose, cancelDisabled));
  } else if (typeof onCancel === 'function') {
    buttons.push(renderDialogButton('cancel', cancelText, onCancel, cancelDisabled));
  }

  buttons.push(
    ...containedButtons.map(([text, info]) =>
      renderDialogButton('other', text, info.handler, undefined, undefined, 'contained'),
    ),
  );

  if (submit) {
    buttons.push(renderDialogButton('submit', submitText ?? 'Ok', submit, submitDisabled, 'dialog-submit'));
  } else if (submitText != null) {
    buttons.push(renderDialogButton('submit', submitText, onClose, submitDisabled, 'dialog-submit'));
  }

  return (
    <MaterialDialog
      open
      sx={sx}
      slotProps={slotProps}
      maxWidth={maxWidth ?? 'md'}
      scroll={scrollPaper ? 'paper' : undefined}
      onClose={(event, reason) => {
        if (insistent && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
        onClose();
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter') return;

        event.stopPropagation();

        if (
          !enterKeySubmits ||
          event.defaultPrevented ||
          event.target instanceof HTMLTextAreaElement ||
          event.target instanceof HTMLButtonElement
        ) {
          return;
        }

        event.preventDefault();
        submit?.();
      }}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      disableRestoreFocus={disableRestoreFocus}
      transitionDuration={hasTransition ? undefined : 0}
    >
      {title && <DialogTitle>{title}</DialogTitle>}

      <DialogContent sx={contentSx}>
        {contentIsText ? <DialogContentText>{children}</DialogContentText> : children}
      </DialogContent>

      <DialogActions>
        {actionsNode}
        {spinner}
        {buttons}
      </DialogActions>
    </MaterialDialog>
  );
}
